import type { ComparisonSummary as ComparisonSummaryDto, IndicatorDelta } from '@ederest/contracts';
import { Badge } from '../../shared/ui/Badge.js';
import { Card } from '../../shared/ui/Card.js';
import { DirectionChip } from './DirectionChip.js';
import { COMPONENT_LABELS, INDICATOR_LABELS, formatDay, formatIndicatorValue } from './comparison-format.js';
import './comparison.css';

function Window({
  label,
  from,
  to,
  sessions,
}: {
  label: string;
  from: string;
  to: string;
  sessions: number;
}) {
  return (
    <div className="comparison__window" data-window={label}>
      <span className="comparison__window-label">{label}</span>
      <div className="comparison__window-dates">
        Du {formatDay(from)} au {formatDay(to)}
      </div>
      <div className="comparison__window-sessions">{sessions.toLocaleString('fr-FR')} usagers mesurés</div>
    </div>
  );
}

export function DeltaTile({ delta }: { delta: IndicatorDelta }) {
  return (
    <article className="delta-tile" data-indicator={delta.key}>
      <span className="delta-tile__label">{INDICATOR_LABELS[delta.key]}</span>
      <div className="delta-tile__values">
        <span className="delta-tile__before">{formatIndicatorValue(delta.beforeValue, delta.unit)}</span>
        <span className="delta-tile__after">{formatIndicatorValue(delta.afterValue, delta.unit)}</span>
        <DirectionChip delta={delta} />
      </div>
      {delta.lowConfidence && (
        <p className="delta-tile__note">Peu d'usagers mesurés : chiffre à lire avec prudence</p>
      )}
    </article>
  );
}

/** The two windows, what the version published, and the headline deltas. */
export function ComparisonSummary({ summary }: { summary: ComparisonSummaryDto }) {
  const published = new Date(summary.publishedAt).toLocaleDateString('fr-FR');
  return (
    <div className="comparison__summary" data-testid="comparison-summary">
      <Card
        title={`Version ${summary.releaseVersion} · ${summary.days} jours avant, ${summary.days} jours après`}
      >
        <div className="comparison__windows">
          <Window
            label="Avant"
            from={summary.before.from}
            to={summary.before.to}
            sessions={summary.before.sessions}
          />
          <Window
            label="Après"
            from={summary.after.from}
            to={summary.after.to}
            sessions={summary.after.sessions}
          />
        </div>
        <p className="comparison__rule">
          On compare les {summary.days} jours qui suivent la publication du {published} aux {summary.days}{' '}
          jours qui la précèdent, sur les mêmes parcours et avec les mêmes calculs. Aides publiées dans cette
          version :
        </p>
        {summary.guidances.length > 0 ? (
          <div className="comparison__guidances" aria-label="Guidances publiées dans cette version">
            {summary.guidances.map((guidance) => (
              <Badge key={guidance.guidanceId} tone="brand">
                {COMPONENT_LABELS[guidance.component] ?? guidance.component}
                {guidance.stepName ? ` · ${guidance.stepName}` : ''}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="comparison__rule">
            Cette version n'a ajouté aucune guidance (retrait ou réactivation).
          </p>
        )}
      </Card>
      <Card title="Effet mesuré, tous parcours confondus">
        <div className="comparison__tiles">
          <DeltaTile delta={summary.completionRate} />
          <DeltaTile delta={summary.frictionScore} />
        </div>
      </Card>
    </div>
  );
}
